import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Button } from '@heroui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NoAccountBanner from '@/app/components/account/no-account-banner.tsx';
import { PageLoading } from '@/app/components/page-loading.tsx';
import { useRouteAccount } from '@/app/hooks/use-route-account.hook.ts';
import toast from '@/app/toast.ts';
import ResponsiveTabs from '@/primitives/responsive.tabs.tsx';
import { usePutAccountMutation } from '@/slices/account-api-slice.ts';
import { AccountCategories } from './account-categories.tsx';
import AccountQrCode from './account-qr-code.tsx';
import AccountSettingsPermissions from './account-settings-permissions.tsx';
import { AccountStaffMembers } from './account-staff-members.tsx';
import { ManageSpecialties } from './manage-specialties.tsx';
import ManageStaffMemberCategories from './manage-staff-member-categories.tsx';
import { MeetingLocations } from './meeting-locations.tsx';

const TABS = [
  {
    key: 'meeting-rooms',
    title: 'Meeting Rooms',
  },
  {
    key: 'staff-members',
    title: 'Staff Users',
  },
  {
    key: 'specialties',
    title: 'Specialties',
  },
  {
    key: 'staff-member-categories',
    title: 'Staff Categories',
  },
  {
    key: 'categories',
    title: 'Rep Categories',
  },
  {
    key: 'permissions',
    title: 'Permissions',
  },
  {
    key: 'qr-code',
    title: 'QR Code',
  },
];

export default function AccountSettingsV2() {
  const [tab, setTab] = useState<string>('meeting-rooms');

  const navigate = useNavigate();
  const [putAccount, { isError }] = usePutAccountMutation();
  const { account, isLoading } = useRouteAccount({ basePath: '/account/settings' });

  useEffect(() => {
    if (isError) {
      toast.error('There was an error saving the account settings.');
    }
  }, [isError]);

  if (isLoading) {
    return <PageLoading />;
  }

  if (!account) {
    return (
      <div className='flex flex-col gap-4'>
        <h2 className='text-xl'>Account Settings</h2>
        <NoAccountBanner />
      </div>
    );
  }

  return (
    <div className='flex flex-col md:flex-row gap-4 md:gap-10 max-w-screen-2xl'>
      <div className='flex flex-col items-start md:items-end min-w-fit'>
        <Button
          className='self-start mb-2'
          isIconOnly
          variant='flat'
          color='primary'
          onPress={() => navigate(-1)}
        >
          <ArrowLeftIcon className='size-4' />
        </Button>
        <h4 className='text-primary-600'>Account Settings</h4>
        <h6 className='text-label mb-10'>{account.accountName}</h6>
        <ResponsiveTabs options={TABS} tab={tab} setTab={setTab} />
      </div>
      <div className='flex-1'>
        {tab === 'meeting-rooms' && <MeetingLocations account={account} />}
        {tab === 'staff-member-categories' && <ManageStaffMemberCategories account={account} />}
        {tab === 'staff-members' && <AccountStaffMembers account={account} />}
        {tab === 'specialties' && <ManageSpecialties account={account} />}
        {tab === 'categories' && <AccountCategories account={account} />}
        {tab === 'permissions' && (
          <AccountSettingsPermissions account={account} putAccount={putAccount} />
        )}
        {tab === 'qr-code' && (
          <AccountQrCode accountKey={account.key} accountName={account.accountName} />
        )}
      </div>
    </div>
  );
}
